import React from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  CircularProgress,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../contexts/OrgContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export default function OrgPickerPage() {
  useDocumentTitle('Organizations');

  const { organizations, loading, switchOrg } = useOrg();
  const navigate = useNavigate();

  const handleSelectOrg = async (orgId: string) => {
    await switchOrg(orgId);
    navigate('/org/guides');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, bgcolor: 'background.default' }}>
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 500, mb: 1 }}>
          Organizations
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Select an organization to manage its guides.
        </Typography>

        {organizations.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <BusinessIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No organizations yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create an organization to publish guides under a unique URL.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/org/new')}
            >
              Create Organization
            </Button>
          </Paper>
        ) : (
          <>
            <Stack spacing={0}>
              {organizations.map((org) => (
                <Paper
                  key={org.id}
                  variant="outlined"
                  onClick={() => handleSelectOrg(org.id)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': {
                      borderColor: 'primary.light',
                      bgcolor: 'action.hover',
                    },
                    borderRadius: 0,
                    '&:first-of-type': { borderTopLeftRadius: 8, borderTopRightRadius: 8 },
                    '&:last-of-type': { borderBottomLeftRadius: 8, borderBottomRightRadius: 8 },
                    '&:not(:first-of-type)': { marginTop: '-1px' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <BusinessIcon sx={{ color: 'primary.main' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        {org.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {org.slug}
                      </Typography>
                    </Box>
                    <ArrowForwardIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                  </Box>
                </Paper>
              ))}
            </Stack>
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => navigate('/org/new')}
              >
                Create Organization
              </Button>
            </Box>
          </>
        )}
      </Container>
    </Box>
  );
}
